import React from 'react';
import { PiListBulletsLight, PiScalesLight } from 'react-icons/pi';
import LabelTextArea from 'src/modules/shared/components/atoms/inputs/label-text-area';
import { Text } from 'src/modules/shared/components/atoms/text';
import { InlineFormSection } from 'src/modules/shared/components/molecules/inline-form-section/inline-form-section';
import { SectionHeading } from 'src/modules/shared/components/molecules/section-heading/section-heading';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';

/** Text of each optional content section, one item per line. */
interface WebsiteContentSectionTexts {
  /** Featured areas. */
  neighborhoods: string;
  /** Services. */
  services: string;
  /** Questions and answers as `Question | Answer` lines. */
  faqs: string;
  /** Links as `Label | URL` lines. */
  externalLinks: string;
  /** Legal notice. */
  legal: string;
  /** Privacy text of the legal page. */
  privacy: string;
}

/** Props for the content step. */
interface WebsiteContentStepProps {
  /** Current text of each section. */
  texts: WebsiteContentSectionTexts;
  /** Whether the current user may not edit the website. */
  disabled: boolean;
  /** Called when the featured areas change. */
  onNeighborhoodsChange: (value: string) => void;
  /** Called when the services change. */
  onServicesChange: (value: string) => void;
  /** Called when the questions change. */
  onFaqsChange: (value: string) => void;
  /** Called when the external links change. */
  onExternalLinksChange: (value: string) => void;
  /** Called when the legal notice changes. */
  onLegalChange: (value: string) => void;
  /** Called when the privacy text changes. */
  onPrivacyChange: (value: string) => void;
}

/**
 * Content step: the optional sections of the public site, plus the texts of
 * its legal and privacy page. Empty sections are left out of the site.
 *
 * @param props - Section texts and change callbacks.
 * @returns The content step panel.
 */
export const WebsiteContentStep: React.FC<WebsiteContentStepProps> = ({
  texts,
  disabled,
  onNeighborhoodsChange,
  onServicesChange,
  onFaqsChange,
  onExternalLinksChange,
  onLegalChange,
  onPrivacyChange,
}) => {
  const { t } = useI18n();

  return (
    <section className="space-y-4">
      <SectionHeading
        title={t('websites.settings.content.title')}
        description={t('websites.settings.content.description')}
      />
      <InlineFormSection
        title={t('websites.settings.content.sections.title')}
        description={t('websites.settings.content.formatHint')}
        icon={<PiListBulletsLight className="h-5 w-5" aria-hidden />}
      >
        <div className="grid gap-4">
          <LabelTextArea
            label={t('websites.settings.content.neighborhoods.label')}
            labelPosition="top"
            inputProps={{
              rows: 5,
              value: texts.neighborhoods,
              placeholder: t(
                'websites.settings.content.neighborhoods.placeholder'
              ),
              disabled,
              onChange: (event) => onNeighborhoodsChange(event.target.value),
            }}
          />
          <LabelTextArea
            label={t('websites.settings.content.services.label')}
            labelPosition="top"
            inputProps={{
              rows: 5,
              value: texts.services,
              placeholder: t('websites.settings.content.services.placeholder'),
              disabled,
              onChange: (event) => onServicesChange(event.target.value),
            }}
          />
          <LabelTextArea
            label={t('websites.settings.content.faqs.label')}
            labelPosition="top"
            inputProps={{
              rows: 5,
              value: texts.faqs,
              placeholder: t('websites.settings.brochure.faqs.placeholder'),
              disabled,
              onChange: (event) => onFaqsChange(event.target.value),
            }}
          />
          <LabelTextArea
            label={t('websites.settings.content.externalLinks.label')}
            labelPosition="top"
            inputProps={{
              rows: 5,
              value: texts.externalLinks,
              placeholder: t(
                'websites.settings.brochure.externalLinks.placeholder'
              ),
              disabled,
              onChange: (event) => onExternalLinksChange(event.target.value),
            }}
          />
        </div>
      </InlineFormSection>
      <InlineFormSection
        title={t('websites.settings.content.legalPage.title')}
        description={t('websites.settings.content.legalPage.description')}
        icon={<PiScalesLight className="h-5 w-5" aria-hidden />}
      >
        <LabelTextArea
          label={t('websites.settings.content.legal.label')}
          labelPosition="top"
          inputProps={{
            rows: 3,
            'aria-label': t('websites.settings.content.legal.label'),
            value: texts.legal,
            placeholder: t('websites.settings.content.legal.placeholder'),
            disabled,
            onChange: (event) => onLegalChange(event.target.value),
          }}
        />
        <div className="space-y-1.5">
          <LabelTextArea
            label={t('websites.settings.content.privacy.label')}
            labelPosition="top"
            inputProps={{
              rows: 10,
              'aria-label': t('websites.settings.content.privacy.label'),
              value: texts.privacy,
              disabled,
              onChange: (event) => onPrivacyChange(event.target.value),
            }}
          />
          <Text size="tiny" color="secondary">
            {t('websites.settings.content.privacy.help')}
          </Text>
        </div>
      </InlineFormSection>
    </section>
  );
};
